import { useEffect, useState } from "react";
import { Mail, X, Loader2, ShieldCheck, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Verify-email banner.
 * - If our email provider is temporarily unable to send (sandbox mode),
 *   we surface a one-click "Verify now" that confirms the address in-app.
 * - Otherwise, standard resend-link flow.
 *
 * No app features are gated by verification today — the copy reflects that
 * truthfully ("for security and account recovery") rather than promising
 * unlocks that don't exist.
 */
export default function VerifyEmailBanner() {
    const { t } = useTranslation();
    const { user, refresh, setUser } = useAuth();
    const [status, setStatus] = useState(null);   // { sandbox, message }
    const [busy, setBusy] = useState(false);
    const [resent, setResent] = useState(false);
    const [verified, setVerified] = useState(false);
    const [error, setError] = useState("");
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        api.get("/auth/email-status").then(r => setStatus(r.data)).catch(() => setStatus({ sandbox: false }));
    }, []);

    if (!user?.email) return null;
    if (user.email_verified || verified) return null;
    if (dismissed) return null;

    const sandbox = status?.sandbox;

    const verifyNow = async () => {
        setBusy(true); setError("");
        try {
            await api.post("/auth/me/verify-now");
            // Optimistically hide the banner immediately, then sync with server.
            setVerified(true);
            if (typeof setUser === "function") {
                setUser((prev) => prev ? { ...prev, email_verified: true } : prev);
            }
            if (typeof refresh === "function") {
                refresh().catch(() => {});
            }
        } catch (e) {
            setError(e.response?.data?.detail || t("verify.errCouldNotVerify"));
        } finally { setBusy(false); }
    };

    const resend = async () => {
        setBusy(true); setError("");
        try {
            await api.post("/auth/resend-verification", { email: user.email });
            setResent(true);
        } catch (e) {
            setError(e.response?.data?.detail || t("verify.errCouldNotSend"));
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="verify-banner"
             className="card-warm px-4 py-2.5 mb-4 flex flex-wrap items-center gap-3 text-sm border-l-4"
             style={{ borderLeftColor: sandbox ? "var(--brand-orange)" : "var(--brand-magenta)" }}>
            {sandbox
                ? <Info size={14} className="text-[var(--brand-orange)] shrink-0" />
                : <Mail size={14} className="text-[var(--brand-magenta)] shrink-0" />}
            <div className="min-w-0 flex-1">
                <span className="font-medium">
                    {sandbox ? t("verify.confirmSandbox") : t("verify.confirmDefault")}
                </span>
                <span className="text-[var(--text-ink)]/65 text-xs ml-2" data-testid="verify-banner-detail">
                    {sandbox
                        ? t("verify.detailSandbox", { email: user.email })
                        : (resent ? t("verify.detailSent") : t("verify.detailDefault", { email: user.email }))}
                </span>
                {error && <div className="text-xs text-[var(--status-red)] mt-1" data-testid="verify-banner-error">{error}</div>}
            </div>
            {sandbox ? (
                <button onClick={verifyNow} disabled={busy} data-testid="verify-now-btn"
                        className="btn-primary text-xs inline-flex items-center gap-1.5">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                    {busy ? t("verify.verifying") : t("verify.verifyNow")}
                </button>
            ) : (!resent && (
                <button onClick={resend} disabled={busy} data-testid="verify-resend"
                        className="btn-primary text-xs inline-flex items-center gap-1.5">
                    {busy && <Loader2 size={12} className="animate-spin" />}
                    {busy ? t("common.sending") : t("common.resend")}
                </button>
            ))}
            <button onClick={() => setDismissed(true)} aria-label={t("common.dismiss")}
                    data-testid="verify-banner-dismiss"
                    className="w-8 h-8 grid place-items-center rounded-full text-[var(--text-ink)]/45 hover:text-[var(--brand-magenta)] hover:bg-[rgba(26,19,24,0.05)] active:bg-[rgba(26,19,24,0.08)] transition shrink-0">
                <X size={16} />
            </button>
        </div>
    );
}
